import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Heart, Menu, ShoppingBag, X } from "lucide-react";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "Our Mixes", href: "/mixes" },
  { label: "How to Bake", href: "/#how-to-bake" },
  { label: "Our Story", href: "/our-story" },
  { label: "Baking Tips", href: "/#tips" },
  { label: "Reviews", href: "/#reviews" },
  { label: "FAQ", href: "/#faq" },
];

export const Logo = ({ dark = false }) => (
  <a href="/" data-testid="nav-logo-link" className="inline-flex flex-col items-center leading-none group">
    <span className={`inline-flex items-center font-script text-4xl leading-none ${dark ? "text-[#F8C8DC]" : "text-[#6A2C3E]"} transition-transform duration-300 group-hover:-rotate-3`}>
      Almo
      <Heart className={`w-3.5 h-3.5 ml-1 ${dark ? "text-[#F8C8DC]" : "text-[#C86D51]"} fill-current`} />
    </span>
    <span className={`font-mono text-[9px] tracking-[0.55em] mt-0.5 ml-[0.55em] ${dark ? "text-[#FAF7F2]/70" : "text-[#6E5E57]"}`}>
      BAKES
    </span>
  </a>
);

export default function Nav() {
  const [open, setOpen] = useState(false);
  const { cart, setDrawerOpen } = useCart();

  const cartButton = (
    <button
      data-testid="nav-cart-button"
      onClick={() => setDrawerOpen(true)}
      className="relative p-2 text-[#2C221E] hover:text-[#6A2C3E] transition-colors"
      aria-label="Open basket"
    >
      <ShoppingBag className="w-5 h-5" />
      {cart?.totalQuantity > 0 && (
        <span data-testid="nav-cart-count" className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] rounded-full bg-[#6A2C3E] text-[#FAF7F2] text-[10px] font-mono flex items-center justify-center px-1">
          {cart.totalQuantity}
        </span>
      )}
    </button>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div data-testid="announcement-bar" className="bg-[#2C221E] text-[#FAF7F2] py-2.5 px-4 flex items-center justify-center gap-2.5">
        <Heart className="w-3 h-3 text-[#C86D51] fill-current shrink-0" />
        <p className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em]">Now shipping all across India</p>
        <a href="/mixes" data-testid="announcement-shop-link" className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-[#F8C8DC] hover:text-[#FAF7F2] underline underline-offset-4 transition-colors">
          Shop now
        </a>
      </div>
      <nav className="backdrop-blur-xl bg-[#FAF7F2]/80 border-b border-[#2C221E]/10 max-w-none">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <div className="hidden md:flex items-center gap-4 lg:gap-6 ml-auto">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className="font-mono text-[10px] lg:text-xs uppercase tracking-[0.14em] text-[#6E5E57] hover:text-[#2C221E] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          {cartButton}
        </div>
        <div className="md:hidden flex items-center gap-1">
          {cartButton}
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="p-2 text-[#2C221E]"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden border-t border-[#2C221E]/10 bg-[#FAF7F2]"
          >
            <div className="px-5 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-[#2C221E]"
                >
                  {l.label}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
